import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Newspaper, Bookmark, Calendar, LineChart, Grid3X3,
  TrendingUp, Calculator, GraduationCap, Sparkles, Bell, ChevronRight, X, Shield, Share2, History,
  PanelLeftClose, PanelLeftOpen, NotebookPen, LifeBuoy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { ProfileModal } from '@/components/ProfileModal';
import { SupportFeedbackModal } from '@/components/SupportFeedbackModal';

interface NavItem {
  key: string;
  label: string;
  icon: React.ElementType;
  badge?: number | string | null;
  badgeTone?: 'accent' | 'bear';
  dataTour?: string;
}

const items: NavItem[] = [
  { key: 'dashboard',  label: 'Dashboard',         icon: LayoutDashboard, dataTour: 'sidebar-dashboard' },
  { key: 'news',       label: 'News Feed',         icon: Newspaper, badge: 'dot', badgeTone: 'bear', dataTour: 'sidebar-news' },
  { key: 'bookmarks',  label: 'Bookmarks',         icon: Bookmark, dataTour: 'sidebar-bookmarks' },
  { key: 'calendar',   label: 'Economic Calendar', icon: Calendar, dataTour: 'sidebar-calendar' },
  { key: 'backtest',   label: 'Backtest',          icon: History, dataTour: 'sidebar-backtest' },
  { key: 'journal',    label: 'Trading Journal',   icon: NotebookPen, dataTour: 'sidebar-journal' },
  { key: 'economy',    label: 'Economy',           icon: LineChart, dataTour: 'sidebar-economy' },
  { key: 'cot',        label: 'COT Report',        icon: Grid3X3, dataTour: 'sidebar-cot' },
  { key: 'research',   label: 'Stock Research',    icon: TrendingUp, dataTour: 'sidebar-research' },
  { key: 'lotcalc',    label: 'Lot Calculator',    icon: Calculator, dataTour: 'sidebar-lotcalc' },
  { key: 'tutorials',  label: 'New to Trading',    icon: GraduationCap, dataTour: 'sidebar-tutorials' },
  { key: 'affiliate',  label: 'Affiliate',         icon: Share2, dataTour: 'sidebar-affiliate' },
];

const footerItems: NavItem[] = [
  { key: 'plans',   label: 'Plan',    icon: Sparkles },
  { key: 'updates', label: 'Updates', icon: Bell },
];

const adminItem: NavItem = { key: 'admin', label: 'Admin', icon: Shield };

interface SidebarProps {
  activePanel: string;
  onPanelChange: (panel: string) => void;
  mobileOpen?: boolean;
  onClose?: () => void;
}

const EXPANDED_WIDTH = 256;
const COLLAPSED_WIDTH = 64;

export function Sidebar({ activePanel, onPanelChange, mobileOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const { pinned, togglePinned } = useSidebar();
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const initials = (user?.name || user?.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // Bookmark count → live badge on the Bookmarks nav item.
  const bookmarksQ = useQuery({
    queryKey: ['bookmarks'],
    queryFn: () => api.get<{ bookmarks: unknown[] }>('/api/bookmarks').catch(() => ({ bookmarks: [] })),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const bookmarkCount = Array.isArray(bookmarksQ.data?.bookmarks) ? bookmarksQ.data!.bookmarks.length : 0;

  const navItems = useMemo<NavItem[]>(
    () => items.map((it) =>
      it.key === 'bookmarks'
        ? { ...it, badge: bookmarkCount > 0 ? bookmarkCount : null, badgeTone: 'accent' }
        : it,
    ),
    [bookmarkCount],
  );

  // Visually expanded if pinned OR hovered while unpinned OR on mobile (drawer always full width).
  const expanded = pinned || hovered || !!mobileOpen;

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-bg/70 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        animate={{ width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        initial={false}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        onMouseEnter={() => { if (!pinned) setHovered(true); }}
        onMouseLeave={() => { if (!pinned) setHovered(false); }}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col',
          'bg-bg-elevated/95 backdrop-blur-xl border-r border-border',
          'transition-transform duration-300 ease-out-expo',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
      >
        {/* Logo header */}
        <div className={cn(
          'h-16 flex items-center border-b border-border/60 shrink-0',
          expanded ? 'px-5 justify-between' : 'px-0 justify-center',
        )}>
          <Link
            to="/"
            aria-label="TELOS home"
            className={cn('flex items-center gap-2 min-w-0', !expanded && 'justify-center')}
          >
            <Logo />
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.span
                  key="brand"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                  className="font-bold text-base tracking-wide whitespace-nowrap"
                >TELOS</motion.span>
              )}
            </AnimatePresence>
          </Link>
          <div className="flex items-center gap-1">
            {/* Pin / unpin — desktop only */}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.button
                  key="pin"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  type="button"
                  onClick={togglePinned}
                  aria-label={pinned ? 'Collapse sidebar' : 'Pin sidebar open'}
                  title={pinned ? 'Collapse sidebar' : 'Pin sidebar open'}
                  className={cn(
                    'hidden lg:inline-flex items-center justify-center size-7 rounded-md',
                    'text-fg-subtle hover:text-fg hover:bg-bg-card',
                    'transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                  )}
                >
                  {pinned
                    ? <PanelLeftClose className="size-4" />
                    : <PanelLeftOpen className="size-4" />}
                </motion.button>
              )}
            </AnimatePresence>
            {/* Mobile close */}
            <button
              onClick={onClose}
              className="lg:hidden p-1 -mr-1 rounded text-fg-muted hover:text-fg hover:bg-bg-card"
              aria-label="Close menu"
            ><X className="size-5" /></button>
          </div>
        </div>

        {/* Nav items */}
        <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-0.5', expanded ? 'px-3' : 'px-2')}>
          {navItems.map(item => (
            <NavLink
              key={item.key}
              item={item}
              active={item.key === activePanel}
              expanded={expanded}
              onClick={() => onPanelChange(item.key)}
            />
          ))}
        </nav>

        {/* Footer — order: Plan, Updates, Admin, Support & Feedback */}
        <div className={cn('pt-2 pb-5 border-t border-border/60 space-y-0.5 shrink-0', expanded ? 'px-3' : 'px-2')}>
          {footerItems.map(item => (
            <NavLink
              key={item.key}
              item={item}
              active={item.key === activePanel}
              expanded={expanded}
              onClick={() => onPanelChange(item.key)}
              compact
            />
          ))}
          {user?.role === 'admin' && (
            <NavLink
              item={adminItem}
              active={adminItem.key === activePanel}
              expanded={expanded}
              onClick={() => onPanelChange(adminItem.key)}
              compact
            />
          )}
          {/* Support & Feedback — opens SupportFeedbackModal */}
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            title={!expanded ? 'Support & Feedback' : undefined}
            aria-label="Support & Feedback"
            className={cn(
              'group relative w-full flex items-center rounded-lg text-left text-xs',
              'transition-colors duration-150',
              'text-fg-muted hover:bg-bg-card hover:text-fg',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              expanded ? 'gap-3 px-3 py-2' : 'justify-center px-0 py-2',
            )}
          >
            <LifeBuoy className="size-4 flex-shrink-0 text-fg-subtle group-hover:text-fg-muted" />
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.span
                  key="support-label"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className="font-medium truncate"
                >Support &amp; Feedback</motion.span>
              )}
            </AnimatePresence>
          </button>
          {/* User profile pill — opens ProfileModal */}
          <button
            type="button"
            data-tour="sidebar-profile"
            onClick={() => setProfileOpen(true)}
            aria-label="Open profile"
            title={!expanded ? (user?.name || 'Profile') : undefined}
            className={cn(
              'group mt-2 w-full flex items-center rounded-lg text-left',
              'bg-bg-card border border-border/70',
              'hover:border-border-strong hover:bg-bg-elevated',
              'transition-all duration-150 ease-out-expo',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              expanded ? 'gap-3 px-3 py-2.5' : 'justify-center px-2 py-2',
            )}
          >
            <div className="size-9 rounded-full bg-gradient-to-br from-accent to-accent-subtle flex items-center justify-center text-bg font-bold text-sm shrink-0">
              {initials}
            </div>
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  key="profile-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                  className="flex-1 min-w-0 overflow-hidden"
                >
                  <div className="text-xs font-semibold truncate">{user?.name || 'Guest'}</div>
                  <div className="text-2xs text-fg-subtle truncate">{(() => { const u = user as unknown as { plan?: string; role?: string; promoCode?: string|null; whopMembershipId?: string|null } | null; if (u?.role === 'admin' || (u?.promoCode && !u?.whopMembershipId)) return 'Complimentary Access'; switch (u?.plan) { case 'elite_yearly': return 'Elite Yearly'; case 'elite': return 'Elite Monthly'; case 'pro_yearly': return 'Pro Yearly'; case 'pro': return 'Pro Monthly'; case 'trial': return 'Trial'; default: return 'Free'; } })()}</div>
                </motion.div>
              )}
            </AnimatePresence>
            {expanded && (
              <ChevronRight className="size-4 text-fg-subtle group-hover:text-fg-muted transition-colors shrink-0" />
            )}
          </button>
        </div>
      </motion.aside>

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
      <SupportFeedbackModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

function NavLink({
  item, active, onClick, compact, expanded,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
  compact?: boolean;
  expanded: boolean;
}) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      data-tour={item.dataTour}
      title={!expanded ? item.label : undefined}
      aria-label={item.label}
      className={cn(
        'group relative w-full flex items-center rounded-lg text-left',
        compact ? 'text-xs' : 'text-sm',
        'transition-colors duration-150',
        expanded ? 'gap-3 px-3 py-2' : 'justify-center px-0 py-2',
        active
          ? 'bg-accent/12 text-accent'
          : 'text-fg-muted hover:bg-bg-card hover:text-fg',
      )}
    >
      {active && (
        <motion.span
          layoutId="active-pill"
          className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-accent"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
      <Icon className={cn('size-4 flex-shrink-0', active ? 'text-accent' : 'text-fg-subtle group-hover:text-fg-muted')} />
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.span
            key="label"
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 font-medium truncate"
          >{item.label}</motion.span>
        )}
      </AnimatePresence>
      {/* Both the dot and the count sit in the same fixed-size slot so they line
          up vertically across nav items. */}
      {item.badge === 'dot' && (
        <span
          className={cn(
            expanded
              ? 'inline-flex items-center justify-center w-5 h-[17px]'
              : 'absolute top-1 right-1 inline-flex items-center justify-center w-3 h-3',
          )}
        >
          <span
            className="size-2 rounded-full bg-bear animate-pulse-soft"
            aria-label="Unread"
          />
        </span>
      )}
      {typeof item.badge === 'number' && item.badge > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full text-2xs font-bold tabular leading-none',
            (item.badgeTone === 'bear' ? 'bg-bear text-bg' : 'bg-accent text-bg'),
            expanded
              ? 'min-w-[18px] h-[17px] px-1.5 pb-px'
              : 'absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 pb-px',
          )}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </button>
  );
}

function Logo() {
  return (
    <svg width="22" height="22" viewBox="8 8 60 60" fill="none" className="text-accent shrink-0">
      <circle cx="38" cy="38" r="30" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.3" />
      <circle cx="38" cy="38" r="20" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.3" />
      <circle cx="38" cy="38" r="12" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="38" cy="38" r="4.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="38" cy="38" r="1.8" fill="currentColor" />
      <line x1="38" y1="8" x2="38" y2="24" stroke="currentColor" strokeWidth="1.2" />
      <line x1="38" y1="52" x2="38" y2="68" stroke="currentColor" strokeWidth="1.2" />
      <line x1="8" y1="38" x2="24" y2="38" stroke="currentColor" strokeWidth="1.2" />
      <line x1="52" y1="38" x2="68" y2="38" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
